import { NewUser } from "../components/AdminPanel/NewUser";
import { MAIN_URL, Subdomains, ReplaceParams, UserEndpoints, AdminEndpoints, PublicEndpoints } from "../constants";
import type {
  User,
  LoginData,
  SearchedUser,
  RegisterData,
  UserImageData,
  CheckStatusDataRegNum,
  ValidateUserRegNumData,
  TokenDataPayload,
  AuthenticateData,
  ValidateUserRegNumPayload,
  RegistrationPayload,
  DeleteAttempsPayload,
  Permissions,
  CheckStatusDataIsraeliId,
  ValidateUserIsraeliIdData,
  ValidateUserIsraeliIdPayload,
} from "../types";
import axios from "axios";

axios.defaults.withCredentials = true;

export async function getAppInfo() {
  return await axios<PublicEndpoints.info>({
    method: "GET",
    url: `${MAIN_URL}`,
  });
}

export async function getTokenData(token: string) {
  return await axios<TokenDataPayload>({
    method: "GET",
    url: `${MAIN_URL}${PublicEndpoints.portal}?token=${token}`,
  });
}

export async function checkStatusRegNum(checkStatusData: CheckStatusDataRegNum) {
  return await axios<{ status: string }>({
    method: "GET",
    url: `${MAIN_URL}${Subdomains.users}${UserEndpoints.statusCheckRegNum}`
      .replace(ReplaceParams.registrationNumber, String(checkStatusData.registrationNumber))
      .replace(ReplaceParams.birthDate, checkStatusData.birthDate)
      .replace(ReplaceParams.language, checkStatusData.language),
  });
}
export async function checkStatusIsraeliId(checkStatusData: CheckStatusDataIsraeliId) {
  return await axios<{ status: string }>({
    method: "GET",
    url: `${MAIN_URL}${Subdomains.users}${UserEndpoints.statusCheckIsraeliId}`
      .replace(ReplaceParams.israeliId, String(checkStatusData.israeliId))
      .replace(ReplaceParams.birthDate, checkStatusData.birthDate)
      .replace(ReplaceParams.language, checkStatusData.language),
  });
}

export async function getAllRolesAPI() {
  return await axios({
    method: "GET",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.getRoles}`,
  });
}

export async function addNewUserAPI(user: NewUser) {
  return await axios<DeleteAttempsPayload>({
    method: "POST",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.newUser}`,
    data: user,
  });
}

export async function deleteAttempsAPI(user: User) {
  return await axios<DeleteAttempsPayload>({
    method: "POST",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.delete}`,
    data: user,
  });
}
export async function switchUserStatusAPI(regNum: number) {
  return await axios<DeleteAttempsPayload>({
    method: "PUT",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.switchStatus}`,
    data: { registrationNumber: regNum },
  });
}

export async function validateUserByRegNum(validateUserData: ValidateUserRegNumData) {
  return await axios<ValidateUserRegNumPayload>({
    method: "POST",
    url: `${MAIN_URL}${Subdomains.users}${UserEndpoints.validationByRegNum}`,
    data: validateUserData,
  });
}

export async function validateUserByIsraeliId(validateUserData: ValidateUserIsraeliIdData) {
  return await axios<ValidateUserIsraeliIdPayload>({
    method: "POST",
    url: `${MAIN_URL}${Subdomains.users}${UserEndpoints.validationByIsraeliId}`,
    data: validateUserData,
  });
}

export async function registerUser(registerData: RegisterData) {
  return await axios<RegistrationPayload>({
    method: "POST",
    url: `${MAIN_URL}${Subdomains.users}${UserEndpoints.registration}`,
    data: registerData,
  });
}

export async function authenticateUser(authenticateData: AuthenticateData) {
  return await axios<RegistrationPayload>({
    method: "POST",
    url: `${MAIN_URL}${Subdomains.users}${UserEndpoints.authentication}`,
    data: authenticateData,
  });
}

export async function loginReturningAdmin() {
  return await axios<{
    role: number;
    username: string;
    permissions: Permissions;
  }>({
    method: "GET",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.reLogIn}`,
  });
}

export async function loginAdmin(loginData: LoginData) {
  return await axios<{
    role: number;
    username: string;
    permissions: Permissions;
  }>({
    method: "POST",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.login}`,
    data: loginData,
  });
}

export async function logoutAdmin() {
  return await axios({
    method: "GET",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.logout}`,
  });
}

export async function getUsers(page: string) {
  return await axios<{ users: User[]; numberOfEntries: number }>({
    method: "GET",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.allEntries}`.replace(ReplaceParams.page, page),
  });
}

export async function getSingleUser(registrationNumber: string) {
  return await axios<{ users: User[] }>({
    method: "GET",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.singleEntry}`.replace(
      ReplaceParams.registrationNumber,
      registrationNumber
    ),
  });
}

export async function getUserImages(id: string) {
  return await axios<UserImageData>({
    method: "GET",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.images}`.replace(ReplaceParams.id, id),
  });
}

export async function searchUserByRegNum(registrationNumber: string) {
  return await axios<{ user: SearchedUser }>({
    method: "GET",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.searchByRegNum}`.replace(
      ReplaceParams.registrationNumber,
      registrationNumber
    ),
  });
}

export async function searchUserByIsaeliId(israeliId: string) {
  return await axios<{ user: SearchedUser }>({
    method: "GET",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.searchByIsraeliId}`.replace(
      ReplaceParams.israeliId,
      israeliId
    ),
  });
}

export async function resetCount(registrationNumber: number) {
  return await axios({
    method: "PUT",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.countReset}`,
    data: {
      registrationNumber,
    },
  });
}

export async function changePassword(currentPassword: string, password: string) {
  return await axios({
    method: "PUT",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.passwordChange}`,
    data: {
      password,
      currentPassword,
    },
  });
}

export async function getTotalUsers() {
  return await axios<{ paneemUsers: User[]; portalUsers: User[] }>({
    method: "GET",
    url: `${MAIN_URL}${Subdomains.admins}${AdminEndpoints.totals}`,
  });
}
